/**
 * Groups cart line items that belong to the same bundle instance (tracked
 * via the hidden "_bundle_id" line item property, unique per checkout click)
 * into a single collapsible summary row, purely as a DOM presentation layer
 * on top of Dawn's existing cart-drawer / cart page markup. Two bundles of
 * the same tier — even with identical fragrances — get their own summary
 * row instead of being merged, since grouping keys off the unique id rather
 * than the shared "Bundle" label text used only for display.
 *
 * The individual line items stay in the DOM (so "Remove" can target them by
 * variant id) but are always hidden — "See details" reveals a plain,
 * non-interactive list of fragrance names instead, since quantities inside
 * a bundle aren't editable.
 *
 * Summary rows are updated in place rather than destroyed and rebuilt on
 * every re-render, so their click listeners and expanded/collapsed state
 * survive incidental DOM mutations elsewhere in the cart.
 */
(function () {
  function moneyFormat(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function detailsListHTML(rows) {
    return rows
      .map(function (row) {
        var name = row.querySelector('.cart-item__name');
        var qty = parseInt(row.dataset.quantity, 10) || 1;
        var title = name ? name.textContent.trim() : '';
        return '<li>' + qty + 'x ' + title + '</li>';
      })
      .join('');
  }

  function collageImagesHTML(rows) {
    var images = [];
    rows.forEach(function (row) {
      var img = row.querySelector('.cart-item__image');
      if (img && images.length < 3) images.push(img.src);
    });
    return images
      .map(function (src) {
        return '<img src="' + src + '" alt="" loading="lazy" width="60" height="60">';
      })
      .join('');
  }

  function createSummaryRow(bundleId) {
    var tr = document.createElement('tr');
    tr.className = 'cart-item cart-item--bundle-summary';
    tr.dataset.bundleSummaryFor = bundleId;
    tr.innerHTML =
      '<td class="cart-item__media">' +
      '<div class="bundle-summary__images"></div>' +
      '</td>' +
      '<td class="cart-item__details">' +
      '<div class="bundle-summary__title-row">' +
      '<p class="bundle-summary__title h4"></p>' +
      '<button type="button" class="bundle-summary__remove link">Remove</button>' +
      '</div>' +
      '<button type="button" class="bundle-summary__toggle link" aria-expanded="false">' +
      '<span data-label-show>See details</span>' +
      '<span data-label-hide hidden>Hide details</span>' +
      '</button>' +
      '<ul class="bundle-summary__details-list" hidden></ul>' +
      '</td>' +
      '<td class="cart-item__totals right bundle-summary__totals">' +
      '<span class="price price--end"></span>' +
      '</td>';

    var toggle = tr.querySelector('.bundle-summary__toggle');
    var showLabel = toggle.querySelector('[data-label-show]');
    var hideLabel = toggle.querySelector('[data-label-hide]');
    var detailsList = tr.querySelector('.bundle-summary__details-list');
    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      detailsList.hidden = expanded;
      showLabel.hidden = !expanded;
      hideLabel.hidden = expanded;
    });

    tr.querySelector('.bundle-summary__remove').addEventListener('click', function () {
      removeGroupById(tr.dataset.bundleSummaryFor);
    });

    return tr;
  }

  function parseLabelPriceCents(label) {
    var match = label.match(/\$(\d+(?:\.\d{1,2})?)/);
    return match ? Math.round(parseFloat(match[1]) * 100) : null;
  }

  function updateSummaryRow(tr, rows, label) {
    var labelCents = parseLabelPriceCents(label);
    var totalCents = labelCents !== null
      ? labelCents
      : rows.reduce(function (sum, row) {
          return sum + (parseInt(row.dataset.finalLinePrice, 10) || 0);
        }, 0);

    tr.querySelector('.bundle-summary__title').textContent = label;
    tr.querySelector('.bundle-summary__images').innerHTML = collageImagesHTML(rows);
    tr.querySelector('.bundle-summary__details-list').innerHTML = detailsListHTML(rows);
    tr.querySelector('.bundle-summary__totals .price').textContent = moneyFormat(totalCents);
  }

  // Removes every real line item in the group one at a time through the
  // cart's own updateQuantity() — the same code path its native remove
  // button uses — so section refresh, the empty-cart state and focus
  // handling all stay in sync with the rest of Dawn instead of being
  // reimplemented (and possibly getting out of sync) here.
  function removeGroupById(bundleId) {
    function removeNext() {
      var escapedId = window.CSS && CSS.escape ? CSS.escape(bundleId) : bundleId;
      var row = document.querySelector(
        'tr.cart-item[data-bundle-id="' + escapedId + '"]:not(.cart-item--bundle-summary)'
      );
      if (!row) return;

      var cartEl = row.closest('cart-items') || row.closest('cart-drawer-items');
      var removeButtonEl = row.querySelector('cart-remove-button');
      var actualButton = row.querySelector('.cart-remove-button');
      if (!cartEl || typeof cartEl.updateQuantity !== 'function' || !removeButtonEl || !actualButton) return;

      var line = removeButtonEl.dataset.index;
      var variantId = actualButton.dataset.variantId;

      var unsubscribe;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          if (unsubscribe) unsubscribe();
          removeNext();
        });
      }
      cartEl.updateQuantity(line, 0, { currentTarget: null }, undefined, variantId);
    }
    removeNext();
  }

  function groupRoot(root, observer) {
    var table = root.querySelector('table.cart-items');
    if (!table) {
      if (observer) observer.observe(root, { childList: true, subtree: true });
      return;
    }

    // Reconnecting always happens in `finally`, even if something below
    // throws — an observer that stays disconnected after an error is what
    // would leave the cart looking stuck until a full page reload.
    if (observer) observer.disconnect();
    try {
      var rows = Array.prototype.slice.call(
        table.querySelectorAll('tbody tr.cart-item:not(.cart-item--bundle-summary)')
      );

      var groups = {};
      var labels = {};
      var order = [];
      rows.forEach(function (row) {
        var bundleId = row.dataset.bundleId;
        if (!bundleId) {
          row.classList.remove('bundle-item-hidden');
          return;
        }
        if (!groups[bundleId]) {
          groups[bundleId] = [];
          labels[bundleId] = row.dataset.bundleLabel;
          order.push(bundleId);
        }
        groups[bundleId].push(row);
        row.classList.add('bundle-item-hidden');
      });

      // Drop summary rows whose bundle no longer has any matching line items.
      table.querySelectorAll('tbody tr.cart-item--bundle-summary').forEach(function (summary) {
        if (!groups[summary.dataset.bundleSummaryFor]) summary.remove();
      });

      order.forEach(function (bundleId) {
        var groupRows = groups[bundleId];
        var summary = table.querySelector(
          'tbody tr.cart-item--bundle-summary[data-bundle-summary-for="' +
            (window.CSS && CSS.escape ? CSS.escape(bundleId) : bundleId) +
            '"]'
        );
        if (!summary) {
          summary = createSummaryRow(bundleId);
          groupRows[0].parentNode.insertBefore(summary, groupRows[0]);
        } else if (summary.nextElementSibling !== groupRows[0]) {
          groupRows[0].parentNode.insertBefore(summary, groupRows[0]);
        }
        updateSummaryRow(summary, groupRows, labels[bundleId]);
      });
    } catch (e) {
      if (window.console && console.error) console.error('bundle-cart-grouping:', e);
    } finally {
      if (observer) observer.observe(root, { childList: true, subtree: true });
    }
  }

  function watch(rootId) {
    var root = document.getElementById(rootId);
    if (!root) return;
    var observer = new MutationObserver(function () {
      groupRoot(root, observer);
    });
    groupRoot(root, observer);
  }

  function init() {
    watch('CartDrawer');
    watch('main-cart-items');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
